/**
 * The 2-minute check guides (pilot readiness, owner 2026-10-18: "check list guides online and easy to read ... to ensure we cover
 * major violations fast"). One short library guide per check a kitchen runs to keep an often-cited inspection item right, in the
 * same three parts every time:
 *   1. What the inspector looks for   2. The 2-minute check   3. How to fix it
 * They are ordinary library topics (library.ts appends them), so they are online at /library/<id>/, in the app's Library, and in
 * search. Each check in the pilot set (pilotchecks.ts) links its guide.
 *
 * Rules (packages/core/test/pilotchecks.test.ts, each with a control):
 *  - Plain: every section reads at grade 5 or under (Flesch-Kincaid, the landing copy-lint's own counter); short lines, one idea each.
 *  - Sourced: every section cites the Food Code section it follows (FDA Food Code 2022). A state's own lines (Utah's adoption rule,
 *    a county's code number) are added by the state, never written here (state-coupling).
 *  - Walk-together voice (owner, 2026-10-16): no pressure words, no blame; a fix is something a kitchen does today.
 *  - Never invent: a fix that is a judgment call says to ask the person in charge, and "throw it out" when nobody knows how long.
 */
import type { LibraryTopic } from "./library.ts";

const L = (...lines: string[]) => lines.join("\n");

/** The three headings, the same in every guide (the passability persona: nothing new to learn from one guide to the next). */
export const CHECK_GUIDE_HEADINGS = ["What the inspector looks for", "The 2-minute check", "How to fix it"] as const;
const [LOOKS, CHECK, FIX] = CHECK_GUIDE_HEADINGS;
const KEYS = ["inspection checklist", "2-minute check"];

export const CHECK_GUIDES: readonly LibraryTopic[] = [
  {
    id: "check-cold-holding", title: "Cold food: the 2-minute check", category: "temperature",
    summary: "Cold food stays at 41°F or colder. This is the check that keeps it there.",
    sections: [
      { heading: LOOKS, body: L(
        "- The inspector puts a probe in the food, not the air.",
        "- Food you keep cold is at 41°F or colder.",
        "- Each fridge has its own thermometer you can read."), citations: ["Food Code 3-501.16(A)(2)", "Food Code 4-204.112"] },
      { heading: CHECK, body: L(
        "- Open each fridge and cold well.",
        "- Put your probe in the middle of one food. Wait for the number to stop.",
        "- Is it 41°F or less? Good. Write it down.",
        "- Do it when you open and when you close."), citations: ["Food Code 3-501.16(A)(2)"] },
      { heading: FIX, body: L(
        "- Move the food to a cold fridge that works.",
        "- Don't know how long it was warm? Throw it out.",
        "- Do not pack the fridge too full. Cold air has to move.",
        "- Tell the person in charge if the fridge stays warm. Get it fixed."), citations: ["Food Code 3-501.16(A)(2)", "Food Code 4-301.11"] },
    ],
    requirementIds: ["fc-holding"], keywords: KEYS,
  },
  {
    id: "check-hot-holding", title: "Hot food: the 2-minute check", category: "temperature",
    summary: "Hot food stays at 135°F or hotter. This is the check that keeps it there.",
    sections: [
      { heading: LOOKS, body: L(
        "- The inspector probes hot food on the line and in the hot box.",
        "- Hot food is at 135°F or hotter."), citations: ["Food Code 3-501.16(A)(1)"] },
      { heading: CHECK, body: L(
        "- Probe each pan of hot food. Put the tip in the middle.",
        "- Is it 135°F or more? Good. Write it on the cooking and holding log.",
        "- Stir first. The top and sides cool first."), citations: ["Food Code 3-501.16(A)(1)"] },
      { heading: FIX, body: L(
        "- Turn the heat up. Put a lid on.",
        "- Ask the person in charge before you heat food up again.",
        "- Food that was cooked and cooled goes to 165°F within 2 hours.",
        "- Don't know how long it was cool? Throw it out."), citations: ["Food Code 3-501.16(A)(1)", "Food Code 3-403.11"] },
    ],
    requirementIds: ["fc-holding", "fc-reheating"], keywords: KEYS,
  },
  {
    id: "check-cooling", title: "Cooling: the 2-minute check", category: "temperature",
    summary: "Hot food you save for later has to cool fast, in two steps.",
    sections: [
      { heading: LOOKS, body: L(
        "- The inspector looks for deep pots cooling on a shelf or with a lid on.",
        "- They ask how you cool food, and look at your cooling log.",
        "- 135°F to 70°F in 2 hours. Then to 41°F. 6 hours in all."), citations: ["Food Code 3-501.14(A)", "Food Code 3-501.15"] },
      { heading: CHECK, body: L(
        "- Write the time you start.",
        "- At 2 hours, probe it. Is it 70°F or less?",
        "- At 6 hours, probe it. Is it 41°F or less?"), citations: ["Food Code 3-501.14(A)"] },
      { heading: FIX, body: L(
        "- Use shallow pans. Set them in ice water. Stir.",
        "- Leave the lid off until it is cold.",
        "- Not 70°F at 2 hours? Heat it to 165°F and start over, or throw it out.",
        "- Not 41°F at 6 hours? Throw it out."), citations: ["Food Code 3-501.15", "Source protocols: cooling hot food (if missed)"] },
    ],
    requirementIds: ["fc-cooling"], keywords: KEYS,
  },
  {
    id: "check-sanitizer", title: "Sanitizer: the 2-minute check", category: "cleaning",
    summary: "Sanitizer that is too weak does not kill germs. A test strip tells you in seconds.",
    sections: [
      { heading: LOOKS, body: L(
        "- The inspector dips a test strip in your bucket or sink.",
        "- They ask to see your own test strips.",
        "- The strip color shows the strength."), citations: ["Food Code 4-501.114", "Food Code 4-302.14"] },
      { heading: CHECK, body: L(
        "- Test each new mix. Test each bucket.",
        "- Dip the strip as its can says. Match the color to the chart.",
        "- Chlorine: 50 to 200. Quat: what its label says.",
        "- Write the number down. Keep the strip if you like. It is your proof."), citations: ["Food Code 4-501.114", "Food Code 4-302.14"] },
      { heading: FIX, body: L(
        "- Too weak? Dump it. Mix new. Test again.",
        "- Too strong? Add water. Test again.",
        "- Out of strips? Get more today."), citations: ["Food Code 4-501.114", "Food Code 4-302.14"] },
    ],
    requirementIds: ["fc-sanitizing"], keywords: KEYS,
  },
  {
    id: "check-wiping-cloths", title: "Wiping cloths: the 2-minute check", category: "cleaning",
    summary: "A wet cloth lives in sanitizer between uses, so it cleans and does not spread germs.",
    sections: [
      { heading: LOOKS, body: L(
        "- Wet cloths on the counter or in an apron.",
        "- A bucket with weak or dirty water.",
        "- Cloths for raw meat used on other spots."), citations: ["Food Code 3-304.14"] },
      { heading: CHECK, body: L(
        "- Look at each prep spot. Each wet cloth is in a bucket.",
        "- Test each bucket with a strip.",
        "- Dry cloths are dry and clean."), citations: ["Food Code 3-304.14", "Food Code 4-501.114"] },
      { heading: FIX, body: L(
        "- Put the cloth back in the bucket.",
        "- Dirty or weak water? Dump it. Mix new. Test it.",
        "- Change the bucket when it looks dirty."), citations: ["Food Code 3-304.14"] },
    ],
    requirementIds: ["fc-cleaning-tools", "fc-sanitizing"], keywords: KEYS,
  },
  {
    id: "check-hand-sinks", title: "Hand sinks: the 2-minute check", category: "hands",
    summary: "A hand sink that is ready gets used. Soap, towels and warm water, with nothing in the way.",
    sections: [
      { heading: LOOKS, body: L(
        "- No soap. No paper towels.",
        "- Water that is not warm.",
        "- Dishes, food or a bucket in the sink, or a box in front of it."), citations: ["Food Code 5-205.11", "Food Code 6-301.11", "Food Code 6-301.12", "Food Code 5-202.12"] },
      { heading: CHECK, body: L(
        "- Go to each hand sink.",
        "- Soap? Towels? Warm water in a few seconds?",
        "- Nothing in the sink. Nothing in front of it.",
        "- A sign that says to wash hands."), citations: ["Food Code 5-205.11", "Food Code 6-301.14"] },
      { heading: FIX, body: L(
        "- Fill the soap. Load the towels.",
        "- Take out what is in the sink. Move what is in front.",
        "- No warm water? Tell the person in charge now."), citations: ["Food Code 5-205.11", "Food Code 6-301.11", "Food Code 6-301.12"] },
    ],
    requirementIds: ["fc-handwashing"], keywords: KEYS,
  },
  {
    id: "check-date-marking", title: "Dates on food: the 2-minute check", category: "storage",
    summary: "Food you make or open gets a date, and goes out after 7 days.",
    sections: [
      { heading: LOOKS, body: L(
        "- Food made here or opened with no date.",
        "- Food past day 7.",
        "- The day you make it is day 1."), citations: ["Food Code 3-501.17", "Food Code 3-501.18"] },
      { heading: CHECK, body: L(
        "- Look in each fridge.",
        "- Each food you made or opened has a date.",
        "- Find anything past day 7."), citations: ["Food Code 3-501.17"] },
      { heading: FIX, body: L(
        "- No date and you don't know? Throw it out.",
        "- Past day 7? Throw it out.",
        "- Put a date on it when you make it or open it."), citations: ["Food Code 3-501.17", "Food Code 3-501.18"] },
    ],
    requirementIds: ["fc-date-marking"], keywords: KEYS,
  },
  {
    id: "check-food-storage", title: "Food storage: the 2-minute check", category: "storage",
    summary: "Food kept covered, off the floor and in the right order stays clean.",
    sections: [
      { heading: LOOKS, body: L(
        "- Boxes or bags of food on the floor.",
        "- Food with no lid or wrap.",
        "- Raw meat above food that is ready to eat."), citations: ["Food Code 3-305.11", "Food Code 3-302.11"] },
      { heading: CHECK, body: L(
        "- Look in the walk-in, the dry store and the line.",
        "- Lids or wrap on all of it?",
        "- Nothing on the floor? Shelves 6 inches up.",
        "- Raw meat on the low shelves?"), citations: ["Food Code 3-305.11", "Food Code 3-302.11"] },
      { heading: FIX, body: L(
        "- Lift it onto a shelf or rack.",
        "- Cover it.",
        "- Move raw meat to the bottom, under the rest."), citations: ["Food Code 3-305.11", "Food Code 3-302.11"] },
    ],
    requirementIds: ["fc-storage"], keywords: KEYS,
  },
  {
    id: "check-food-contact-surfaces", title: "Food-contact surfaces: the 2-minute check", category: "cleaning",
    summary: "What touches food is clean to look at and to touch, and sanitized.",
    sections: [
      { heading: LOOKS, body: L(
        "- Food bits on the slicer or the can opener blade.",
        "- Stained or cut-up boards.",
        "- Slime in the ice machine.",
        "- Tools used all day with no cleaning."), citations: ["Food Code 4-601.11(A)", "Food Code 4-602.11"] },
      { heading: CHECK, body: L(
        "- Look at and touch each board, blade and tool.",
        "- Look inside the ice machine.",
        "- Clean to see and to touch? Good."), citations: ["Food Code 4-601.11(A)"] },
      { heading: FIX, body: L(
        "- Wash, rinse, sanitize, then let it air dry.",
        "- Tools in use all day: clean them at least every 4 hours.",
        "- Boards too cut up to clean? Get new ones."), citations: ["Food Code 4-602.11", "Food Code 4-901.11", "Food Code 4-501.12"] },
    ],
    requirementIds: ["fc-sanitizing", "fc-cleaning-frequency"], keywords: KEYS,
  },
  {
    id: "check-nonfood-surfaces", title: "Shelves and handles: the 2-minute check", category: "cleaning",
    summary: "Shelves, handles and the outside of machines are wiped often, so dirt does not build up.",
    sections: [
      { heading: LOOKS, body: L(
        "- Dust or grease on shelves.",
        "- Sticky handles on doors and fridges.",
        "- Gunk on the outside of machines and on fridge seals."), citations: ["Food Code 4-601.11(C)", "Food Code 4-602.13"] },
      { heading: CHECK, body: L(
        "- Look at the shelves in each fridge and in dry store.",
        "- Touch the handles.",
        "- Look at the fridge door seals."), citations: ["Food Code 4-602.13"] },
      { heading: FIX, body: L(
        "- Wipe with soap and water. Then dry.",
        "- Pick one day a week to do it."), citations: ["Food Code 4-602.13"] },
    ],
    requirementIds: ["fc-cleaning-frequency"], keywords: KEYS,
  },
  {
    id: "check-facilities", title: "Floors, walls and repairs: the 2-minute check", category: "cleaning",
    summary: "Floors, walls and drains are clean, and broken things get fixed.",
    sections: [
      { heading: LOOKS, body: L(
        "- Dirt under the line and in the corners.",
        "- Grease on walls by the stove.",
        "- Broken tiles, holes, leaks, a door that does not close."), citations: ["Food Code 6-501.11", "Food Code 6-501.12"] },
      { heading: CHECK, body: L(
        "- Look under the line and the shelves.",
        "- Look at the walls, the ceiling tiles and the drains.",
        "- Write down what is broken."), citations: ["Food Code 6-501.12", "Food Code 6-501.11"] },
      { heading: FIX, body: L(
        "- Sweep and mop at close. Mop water goes in the mop sink.",
        "- Tell the person in charge what is broken.",
        "- Write the day it will be fixed."), citations: ["Food Code 6-501.12", "Food Code 6-501.11"] },
    ],
    requirementIds: ["fc-cleaning-frequency"], keywords: KEYS,
  },
  {
    id: "check-pests", title: "Pests: the 2-minute check", category: "pests",
    summary: "A quick look each week finds signs of pests early, and gaps they get in by.",
    sections: [
      { heading: LOOKS, body: L(
        "- Live bugs or mice. Droppings. Chew marks.",
        "- Gaps under doors. Doors propped open.",
        "- Water that sits, and food left out at night."), citations: ["Food Code 6-501.111", "Food Code 6-202.15"] },
      { heading: CHECK, body: L(
        "- Shine a light under the line, behind the fridges and in dry store.",
        "- Look at the back door. Does it close all the way?",
        "- Look at your traps and bait spots."), citations: ["Food Code 6-501.111", "Food Code 6-202.15"] },
      { heading: FIX, body: L(
        "- Throw out food that pests got into.",
        "- Clean the spot. Wash, rinse, sanitize what food touches.",
        "- Call your pest company.",
        "- Close gaps. Keep doors shut."), citations: ["Food Code 6-501.111", "Food Code 6-202.15"] },
    ],
    requirementIds: ["fc-pest-control"], keywords: KEYS,
  },
  {
    id: "check-restroom", title: "Restroom: the 2-minute check", category: "restrooms",
    summary: "The restroom has soap, towels and paper, is clean, and its door closes by itself.",
    sections: [
      { heading: LOOKS, body: L(
        "- No soap, towels or toilet paper.",
        "- A dirty sink or toilet.",
        "- A door that does not close by itself."), citations: ["Food Code 6-301.11", "Food Code 6-301.12", "Food Code 6-302.11", "Food Code 6-501.18", "Food Code 6-202.14"] },
      { heading: CHECK, body: L(
        "- Soap? Towels? Toilet paper?",
        "- Warm water at the sink?",
        "- Clean sink, toilet and floor?",
        "- Let the door go. Does it close?"), citations: ["Food Code 6-301.11", "Food Code 6-302.11", "Food Code 6-202.14"] },
      { heading: FIX, body: L(
        "- Fill what is out.",
        "- Clean it with the restroom set of cloths and mop.",
        "- Door stuck open? Tell the person in charge."), citations: ["Food Code 6-501.18", "Food Code 6-202.14"] },
    ],
    requirementIds: ["fc-toilet-rooms"], keywords: KEYS,
  },
  {
    id: "check-thermometer", title: "Thermometers: the 2-minute check", category: "temperature",
    summary: "A thermometer that reads wrong makes every log wrong. Ice water tells you.",
    sections: [
      { heading: LOOKS, body: L(
        "- No probe thermometer in the kitchen.",
        "- A probe that reads wrong.",
        "- No thermometer in a fridge."), citations: ["Food Code 4-302.12", "Food Code 4-502.11", "Food Code 4-204.112"] },
      { heading: CHECK, body: L(
        "- Fill a cup with ice. Add a little water. Stir.",
        "- Put the probe in for 30 seconds.",
        "- It should read 32°F. Off by 2 or less is fine."), citations: ["Food Code 4-502.11"] },
      { heading: FIX, body: L(
        "- Set it to 32°F the way its maker says.",
        "- Can't set it? Use a new one.",
        "- Check again after a drop."), citations: ["Food Code 4-502.11"] },
    ],
    requirementIds: ["fc-thermometers"], keywords: KEYS,
  },
  {
    id: "check-chemicals", title: "Chemicals: the 2-minute check", category: "cleaning",
    summary: "Chemicals are labeled and kept below and away from food.",
    sections: [
      { heading: LOOKS, body: L(
        "- A spray bottle with no name on it.",
        "- Chemicals on a shelf above food, cups or plates."), citations: ["Food Code 7-102.11", "Food Code 7-201.11"] },
      { heading: CHECK, body: L(
        "- Look at each spray bottle. Does it say what is in it?",
        "- Look at where chemicals sit. Below food and away from it?"), citations: ["Food Code 7-102.11", "Food Code 7-201.11"] },
      { heading: FIX, body: L(
        "- Write the name on the bottle.",
        "- Move chemicals to a low shelf of their own."), citations: ["Food Code 7-102.11", "Food Code 7-201.11"] },
    ],
    requirementIds: ["fc-chemicals"], keywords: KEYS,
  },
  {
    id: "check-staff-drinks", title: "Staff drinks: the 2-minute check", category: "hands",
    summary: "Staff drinks have a lid and a straw, and sit away from food.",
    sections: [
      { heading: LOOKS, body: L(
        "- An open cup next to food being made.",
        "- Food or drinks where food is made."), citations: ["Food Code 2-401.11"] },
      { heading: CHECK, body: L(
        "- Look at each drink in the kitchen.",
        "- Lid and straw? Set down away from food?"), citations: ["Food Code 2-401.11"] },
      { heading: FIX, body: L(
        "- Give a cup with a lid and a straw.",
        "- Pick one low shelf for staff drinks.",
        "- Eat on breaks, away from food."), citations: ["Food Code 2-401.11"] },
    ],
    requirementIds: ["fc-hygiene"], keywords: KEYS,
  },
  {
    id: "check-vomit-plan", title: "Vomit clean-up: the 2-minute check", category: "people",
    summary: "A written plan and a kit on the shelf, so anyone can clean it up the safe way.",
    sections: [
      { heading: LOOKS, body: L(
        "- The inspector asks to see your written clean-up steps.",
        "- They ask where the kit is.",
        "- They ask a cook what to do first."), citations: ["Food Code 2-501.11"] },
      { heading: CHECK, body: L(
        "- Find the written steps. Are they in the binder?",
        "- Open the kit: gloves, mask, apron, paper towels, bags, bleach.",
        "- Ask one cook: what do you do first?"), citations: ["Food Code 2-501.11"] },
      { heading: FIX, body: L(
        "- Print the steps from the app. Put them in the binder.",
        "- Fill the kit. Keep it by the mop sink.",
        "- Walk the team through it at the next shift."), citations: ["Food Code 2-501.11"] },
    ],
    requirementIds: ["fc-vomit-cleanup"], keywords: KEYS,
  },
  {
    id: "check-container-labels", title: "Food containers: the 2-minute check", category: "storage",
    summary: "Food out of its package says what it is, so nobody grabs the wrong one.",
    sections: [
      { heading: LOOKS, body: L(
        "- A bin of flour, sugar or salt with no name on it.",
        "- A squeeze bottle of oil or sauce with no name.",
        "- Food that could be mixed up with something else."), citations: ["Food Code 3-302.12"] },
      { heading: CHECK, body: L(
        "- Look at bins, jars and squeeze bottles.",
        "- Does each one say what is in it?",
        "- Food no one could mix up, like dry pasta, is fine as it is."), citations: ["Food Code 3-302.12"] },
      { heading: FIX, body: L(
        "- Write the name on it with tape and a marker.",
        "- Or print a label from the app."), citations: ["Food Code 3-302.12"] },
    ],
    requirementIds: ["fc-storage"], keywords: KEYS,
  },
  {
    id: "check-equipment-repair", title: "Broken equipment: the 2-minute check", category: "cleaning",
    summary: "Equipment that is broken, cracked or leaking gets written down and fixed.",
    sections: [
      { heading: LOOKS, body: L(
        "- Torn door seals. Cracked bins and lids.",
        "- A leak under a sink. A drip from a pipe.",
        "- Tape that holds a part on."), citations: ["Food Code 4-501.11", "Food Code 5-205.15"] },
      { heading: CHECK, body: L(
        "- Walk the kitchen once a week.",
        "- Look at seals, shelves, bins, sinks and pipes.",
        "- Write down what is broken."), citations: ["Food Code 4-501.11", "Food Code 5-205.15"] },
      { heading: FIX, body: L(
        "- Throw out cracked bins and lids. Get new ones.",
        "- Call for the repair. Write the day it will be fixed.",
        "- Set a bucket under a leak until then. Keep food away from it."), citations: ["Food Code 4-501.11", "Food Code 5-205.15"] },
    ],
    requirementIds: ["fc-cleaning-frequency"], keywords: KEYS, // related: broken, cracked surfaces cannot be kept clean
  },
  {
    id: "check-utensils", title: "Scoops and spoons: the 2-minute check", category: "cleaning",
    summary: "A scoop or spoon in use rests in the right place between uses.",
    sections: [
      { heading: LOOKS, body: L(
        "- A scoop lying in the ice or the flour, handle down.",
        "- A spoon in a cup of old water.",
        "- Tongs on a dirty counter."), citations: ["Food Code 3-304.12"] },
      { heading: CHECK, body: L(
        "- Look at each scoop, spoon and pair of tongs on the line.",
        "- Handle up and out of the food?",
        "- Or on a clean spot?"), citations: ["Food Code 3-304.12"] },
      { heading: FIX, body: L(
        "- Pull it out. Wash it.",
        "- Put it back with the handle up, out of the food.",
        "- Spoons in water: running water, or water at 135°F or hotter."), citations: ["Food Code 3-304.12"] },
    ],
    requirementIds: ["fc-storage"], keywords: KEYS,
  },
];
